import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, EyeOff, KeyRound, RefreshCw, ShieldCheck, Zap } from "lucide-react";
import { Toaster, toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function validatePassword(password, confirmPassword) {
  const errors = {};
  if (password.length < 8) errors.password = "Password must be at least 8 characters.";
  else if (!/\d/.test(password)) errors.password = "Password must include at least one number.";
  if (confirmPassword && password !== confirmPassword) errors.confirm = "Passwords do not match.";
  return errors;
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestNewLink, setRequestNewLink] = useState(false);
  const [serverError, setServerError] = useState("");

  const errors = useMemo(() => validatePassword(password, confirmPassword), [password, confirmPassword]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) return;
    if (errors.password || errors.confirm || !password || !confirmPassword) return;

    setLoading(true);
    setServerError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success("Password updated successfully. Please log in.");
      setTimeout(() => navigate("/auth"), 1200);
    } catch (err) {
      console.error(err);
      setServerError("This reset link has expired or already been used.");
      setRequestNewLink(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 relative overflow-hidden" style={{ background: "var(--surface-page)" }}>
      <Toaster richColors />
      {["var(--accent-gold)", "var(--accent-gold)", "var(--ce-09-serenity)", "var(--ce-05-wonder)"].map((color, index) => (
        <motion.div
          key={color}
          className="absolute rounded-full opacity-20"
          style={{
            width: 150 - index * 20,
            height: 150 - index * 20,
            background: `radial-gradient(circle, ${color}, transparent)`,
            right: `${10 + index * 14}%`,
            top: `${14 + index * 12}%`,
          }}
          animate={{ y: [0, -22, 0], x: [0, -16, 0] }}
          transition={{ duration: 4.5 + index, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="rounded-2xl p-8" style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--accent-gold), var(--accent-gold))" }}>
              <Zap size={20} />
            </div>
            <div>
              <h1 className="font-black text-xl">ArcScript AI</h1>
              <p className="text-[var(--ink-tertiary)] text-sm">Secure your account</p>
            </div>
          </div>

          <h2 className="text-3xl font-black mb-2">Reset your password</h2>
          <p className="text-[var(--ink-secondary)] text-sm leading-relaxed mb-8">
            Choose a fresh password for your writing studio. Make it strong enough to keep your drafts safe.
          </p>

          {!token ? (
            <div className="space-y-5">
              <div
                className="rounded-xl px-4 py-4 text-sm leading-relaxed"
                style={{ background: "rgba(184, 117, 107, 0.12)", border: "1px solid rgba(184, 117, 107, 0.35)", color: "var(--ink-primary)" }}
                data-testid="reset-password-error"
              >
                Invalid reset link. Please request a new one.
              </div>
              <Link
                to="/forgot-password"
                data-testid="reset-password-request-new"
                className="inline-flex items-center gap-2 text-sm font-medium text-[var(--accent-gold)] hover:text-[var(--accent-gold-hover)] transition-colors"
              >
                <RefreshCw size={14} />
                Request New Link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[var(--ink-secondary)] text-sm mb-1.5">New Password</label>
                <div className="relative">
                  <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-tertiary)]" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Create a stronger password"
                    data-testid="reset-password-new"
                    className="w-full pl-11 pr-11 py-3 rounded-xl placeholder-[var(--ink-tertiary)] text-sm outline-none transition-colors"
                    style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-tertiary)] hover:text-[var(--ink-secondary)]"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && <p className="text-[var(--semantic-error)] text-xs mt-2">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-[var(--ink-secondary)] text-sm mb-1.5">Confirm Password</label>
                <div className="relative">
                  <ShieldCheck size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-tertiary)]" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Re-enter your new password"
                    data-testid="reset-password-confirm"
                    className="w-full pl-11 pr-11 py-3 rounded-xl placeholder-[var(--ink-tertiary)] text-sm outline-none transition-colors"
                    style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-tertiary)] hover:text-[var(--ink-secondary)]"
                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.confirm && <p className="text-[var(--semantic-error)] text-xs mt-2">{errors.confirm}</p>}
              </div>

              {serverError && (
                <div
                  className="rounded-xl px-4 py-4 text-sm leading-relaxed"
                  style={{ background: "rgba(184, 117, 107, 0.12)", border: "1px solid rgba(184, 117, 107, 0.35)", color: "var(--ink-primary)" }}
                  data-testid="reset-password-error"
                >
                  {serverError}
                </div>
              )}

              {requestNewLink && (
                <Link
                  to="/forgot-password"
                  data-testid="reset-password-request-new"
                  className="inline-flex items-center gap-2 text-sm font-medium text-[var(--accent-gold)] hover:text-[var(--accent-gold-hover)] transition-colors"
                >
                  <RefreshCw size={14} />
                  Request New Link
                </Link>
              )}

              <motion.button
                type="submit"
                disabled={loading || !password || !confirmPassword || Boolean(errors.password || errors.confirm)}
                data-testid="reset-password-submit"
                whileHover={{ scale: loading ? 1 : 1.01 }}
                whileTap={{ scale: loading ? 1 : 0.99 }}
                className="w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                style={{ background: loading ? "var(--surface-raised)" : "var(--accent-gold)" }}
              >
                {loading ? (
                  <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <>
                    <ShieldCheck size={16} />
                    Reset Password
                  </>
                )}
              </motion.button>

              <Link
                to="/auth"
                className="inline-flex items-center gap-2 text-sm text-[var(--ink-secondary)] hover: transition-colors"
              >
                <ArrowLeft size={14} />
                Back to Login
              </Link>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}